<template>
  <v-dialog v-model="dialog" max-width="1200px" scrollable>
    <v-card>
      <v-card-title class="d-flex align-center">
        <span class="text-h5">Debug Information - {{ deviceName }}</span>
        <v-spacer />
        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text style="height: 70vh">
        <v-container v-if="$apollo.loading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" size="48" />
          <div class="mt-4">Loading debug data...</div>
        </v-container>

        <div v-else-if="debugData">
          <!-- Summary Section -->
          <v-expansion-panels v-model="expandedPanels" multiple>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <strong>Summary</strong>
                <template v-slot:actions>
                  <v-icon color="primary">mdi-information-outline</v-icon>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <pre class="debug-json">{{ debugData.summary }}</pre>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- States Section -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <strong>States & Status</strong>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <pre class="debug-json">{{ debugData.states }}</pre>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Computed Values Section -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <strong>Computed Values</strong>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <pre class="debug-json">{{ debugData.computed }}</pre>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Database Tables Section -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <strong>Database Tables</strong>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-tabs v-model="activeTab" vertical>
                  <v-tab v-for="(tableData, tableName) in parsedTables" :key="tableName">
                    {{ tableName }}
                    <v-chip v-if="Array.isArray(tableData)" small class="ml-2">
                      {{ tableData.length }}
                    </v-chip>
                  </v-tab>

                  <v-tab-item v-for="(tableData, tableName) in parsedTables" :key="tableName">
                    <v-card flat>
                      <v-card-title class="text-h6">{{ tableName }}</v-card-title>
                      <v-card-text>
                        <pre class="debug-json">{{ JSON.stringify(tableData, null, 2) }}</pre>
                      </v-card-text>
                    </v-card>
                  </v-tab-item>
                </v-tabs>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Metadata -->
          <v-card flat class="mt-4">
            <v-card-subtitle class="text-caption grey--text">
              Generated: {{ debugData.timestamp }}
              <br />
              Device ID: {{ debugData.deviceId }}
            </v-card-subtitle>
          </v-card>
        </div>

        <v-alert v-else-if="error" type="error"> Failed to load debug data: {{ error }} </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" text @click="dialog = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { adminBillingDebug } from '@/graphql/query/adminBillingDebug'

export default {
  name: 'BillingDebugModal',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    device: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      debugData: null,
      error: null,
      expandedPanels: [0], // Expand summary by default
      activeTab: 0,
    }
  },

  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },

    deviceName() {
      return this.device?.customName || 'Unnamed Device'
    },

    deviceGlobalId() {
      return this.device?.id
    },

    parsedTables() {
      if (!this.debugData?.tables) return {}
      try {
        return JSON.parse(this.debugData.tables)
      } catch (error) {
        console.error('Failed to parse tables JSON:', error)
        return {}
      }
    },
  },

  watch: {
    dialog(isOpen) {
      if (isOpen && this.device) {
        this.loadDebugData()
      } else {
        this.resetData()
      }
    },
  },

  methods: {
    async loadDebugData() {
      if (!this.deviceGlobalId) {
        this.error = 'No device selected'
        return
      }

      try {
        this.error = null
        const result = await this.$apollo.query({
          query: adminBillingDebug,
          variables: {
            deviceId: this.deviceGlobalId,
          },
          fetchPolicy: 'network-only', // Always fetch fresh data
        })

        this.debugData = result.data.adminBillingDebug
      } catch (error) {
        console.error('Failed to load debug data:', error)
        this.error = error.message
      }
    },

    resetData() {
      this.debugData = null
      this.error = null
      this.expandedPanels = [0]
      this.activeTab = 0
    },
  },
}
</script>

<style scoped>
.debug-json {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 12px;
  font-family: 'Roboto Mono', 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.4;
  color: #495057;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.v-card {
  border-radius: 8px;
}

.v-expansion-panel-header {
  font-weight: 500;
}

.v-tab {
  text-transform: none !important;
  justify-content: flex-start;
}
</style>
